import type { BillingType, JobDetail, JobInvoiceStatus } from '@jobos/shared/types/job';

interface InvoiceHistoryEntry {
  period: string;
  number: string;
}

// Format period for short display (e.g., "2025-01" -> "Jan 2025")
export function formatPeriodShort(period: string): string {
  const parts = period.split('-');
  const year = parts[0] || '2025';
  const month = parts[1] || '01';
  const date = new Date(parseInt(year), parseInt(month) - 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

// Format invoice number for display - handles both one-time and recurring jobs
export function formatInvoiceDisplay(
  invoiceNumber: string | null | undefined,
  billingType: BillingType | null | undefined
): string {
  if (!invoiceNumber) return '-';

  const isRecurring = billingType === 'monthly' || billingType === 'quarterly';

  if (!isRecurring) {
    return invoiceNumber;
  }

  // Try to parse as JSON array for recurring jobs. Anything that isn't an
  // array is a raw invoice number, not history — including a plain numeric
  // one like "3951", which JSON.parse returns as a number instead of throwing.
  let parsed: unknown;
  try {
    parsed = JSON.parse(invoiceNumber);
  } catch {
    // Not valid JSON, return as-is
    return invoiceNumber;
  }

  if (!Array.isArray(parsed)) return invoiceNumber;

  const history = parsed as InvoiceHistoryEntry[];
  // Get the most recent entry (already sorted newest first)
  const latest = history[0];
  if (latest) {
    const periodDisplay = billingType === 'quarterly'
      ? latest.period.replace('-', ' ')
      : formatPeriodShort(latest.period);
    return `${latest.number} (${periodDisplay})`;
  }

  return '-';
}

// Calculate days since invoice was sent for one-time billing
export function getDaysSinceInvoiceSent(
  lastInvoicedDate: string | null | undefined,
  billingType: BillingType | null | undefined,
  invoiceStatus: JobInvoiceStatus | null | undefined,
): string | null {
  // Only show for one-time billing where the invoice has actually been sent.
  // lastInvoicedDate can persist on reverted jobs, so gate on status too.
  //
  // KEEP IN SYNC WITH InvoiceStatusBadge.isOverdue(), which applies the same
  // 30-day rule to the same column for the badge. An earlier version of this
  // comment said that component "is gone" and had been replaced by the derived
  // state; it is still live, still hardcodes 30 days, and still has to learn
  // about every new sent-ish status at the same time this does.
  //
  // ONLY invoice_sent. Under the action-prompt ladder, partially_paid means
  // everything that went out HAS been paid and the remaining work is unbilled,
  // so there is no outstanding invoice to count days against. invoice_sent is
  // now the single state carrying an unpaid balance, which is what this column
  // measures.
  if (invoiceStatus !== 'invoice_sent') return null;
  const isRecurring = billingType === 'monthly' || billingType === 'quarterly';
  if (isRecurring || !lastInvoicedDate) return null;

  const sentDate = new Date(lastInvoicedDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  sentDate.setHours(0, 0, 0, 0);

  const diffDays = Math.floor((today.getTime() - sentDate.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'today';
  if (diffDays === 1) return '1d';
  return `${diffDays}d`;
}

// Get the relevant date and label based on job status
export function getStatusDateWithLabel(job: JobDetail): { label: string; date: string } | null {
  switch (job.status) {
    case 'quote_requested':
      return job.noticeDate ? { label: 'Notice', date: job.noticeDate } : null;
    case 'estimate_sent':
    case 'awarded':
    case 'not_awarded':
      return job.estimateSendDate ? { label: 'Sent', date: job.estimateSendDate } : null;
    case 'in_progress':
      return job.startDate ? { label: 'Started', date: job.startDate } : null;
    case 'completed':
      return job.completedDate ? { label: 'Completed', date: job.completedDate } : null;
    default:
      return null;
  }
}
